import { createApp } from 'vue'
import { createPinia } from 'pinia'
import ElementPlus from 'element-plus'

// === 页面刷新诊断钩子（调试完成后删除） ===
// 使用 localStorage 持久化记录，因为整页刷新会清空控制台

// 页面加载时，检查上次刷新原因并输出到控制台
;(function checkLastReload() {
  const last = localStorage.getItem('_diag_reload')
  if (last) {
    const info = JSON.parse(last)
    console.error('%c[诊断] 上次页面刷新原因:', 'color:red;font-size:14px', info)
    localStorage.removeItem('_diag_reload')
  }
})()

// 记录 beforeunload（仅在没有更具体原因时才记录，避免覆盖 vite:beforeFullReload）
window.addEventListener('beforeunload', (event) => {
  const existing = localStorage.getItem('_diag_reload')
  if (!existing) {
    localStorage.setItem('_diag_reload', JSON.stringify({
      type: 'beforeunload',
      url: location.href,
      time: new Date().toISOString()
    }))
  }
})

// Vite HMR 全量刷新（最常见的罪魁祸首）
if (import.meta.hot) {
  import.meta.hot.on('vite:beforeFullReload', (data) => {
    const changedFiles = data?.updates?.map(u => u.path) || data?.path || 'unknown'
    console.error('%c[诊断] Vite全量刷新！变化的文件:', 'color:red;font-size:16px', changedFiles)
    console.error('[诊断] 完整data:', data)
    localStorage.setItem('_diag_reload', JSON.stringify({
      type: 'vite:beforeFullReload',
      changedFiles: changedFiles,
      dataType: data?.type,
      url: location.href,
      time: new Date().toISOString()
    }))
  })
  import.meta.hot.on('vite:beforeUpdate', (data) => {
    console.log('[诊断] Vite HMR 热更新:', data.updates?.map(u => u.path))
  })
  import.meta.hot.on('vite:error', (data) => {
    console.error('[诊断] Vite HMR 错误:', data)
    localStorage.setItem('_diag_reload', JSON.stringify({
      type: 'vite:error',
      error: data?.message || String(data),
      url: location.href,
      time: new Date().toISOString()
    }))
  })
}
// === 诊断钩子结束 ===

import App from './App.vue'
import router from './router'
import i18n from '@/locales'

// 样式导入顺序：浅色变量 → Element Plus 默认 → EP暗色 → 我方暗色覆盖 → 组件覆盖 → 全局 → 修复
import '@/assets/styles/theme.scss'  // 1. 主题变量（浅色 :root）
import 'element-plus/dist/index.css'  // 2. Element Plus 默认样式
import 'element-plus/theme-chalk/dark/css-vars.css'  // 3. EP 暗色变量（html.dark）
import '@/assets/styles/theme-dark.scss'  // 4. 我方暗色覆盖（html.dark，覆盖EP暗色默认值）
import '@/assets/styles/element-plus-override.scss'  // 5. Element Plus 组件样式覆盖
import '@/assets/styles/index.scss'  // 6. 全局样式（含工具类）
import '@/styles/element-plus-fixes.css'  // 7. 兼容性修复

// VxeTable 高级表格（先加载默认CSS，再用自定义主题覆盖）
import { setupVxeTable } from '@/plugins/vxe-table'
import '@/assets/styles/vxe-table-theme.scss'  // 8. VxeTable 主题适配（必须在 vxe-table CSS 之后）

// vue-print-next 打印
import { vPrint } from 'vue-print-next'

// 权限控制：路由守卫已在 router/index.js 中统一注册，无需重复导入 permission.js

// 自定义指令
import directives from '@/directives'

// Lucide Icons 图标库（统一使用 Lucide Icons，不再使用 Element Plus Icons）
import * as LucideIcons from 'lucide-vue-next'

const app = createApp(App)

// 注册 Lucide Icons（全局注册常用图标，包含菜单所需的所有图标）
const commonLucideIcons = [
  // 基础图标
  'User', 'Users', 'Settings', 'Home', 'Menu', 'X', 'Search', 'Bell', 'Database',
  'ChevronDown', 'ChevronRight', 'ChevronLeft', 'ChevronUp', 'ChevronsDown', 'ChevronsUp',
  'LogOut', 'LogIn',
  // 文件和文档
  'FileText', 'File', 'Files', 'Folder', 'FolderOpen', 'Download', 'Upload',
  'Paperclip', 'FileSpreadsheet',
  'BookOpen', 'Code',
  // 操作图标
  'Check', 'CheckCircle', 'XCircle', 'Eye', 'EyeOff', 'Edit', 'Trash2', 'Trash',
  'Plus', 'PlusCircle', 'Minus', 'RefreshCw', 'RefreshCcw', 'RotateCcw', 'Save', 'Copy',
  // 导航和布局
  'LayoutDashboard', 'Grid', 'Grid3x3', 'List', 'MoreVertical', 'MoreHorizontal',
  'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'ArrowUpDown', 'ArrowRightLeft',
  'Layers', 'Navigation', 'Smartphone',
  // 数据和图表
  'TrendingUp', 'TrendingDown', 'BarChart', 'BarChart3', 'PieChart', 'LineChart',
  // 通知和警告
  'AlertCircle', 'AlertTriangle', 'Info', 'HelpCircle', 'Shield', 'ShieldAlert',
  // 时间和日期
  'Calendar', 'Clock', 'Timer',
  // 其他
  'Key', 'Lock', 'Unlock', 'Map', 'Package', 'Phone', 'Zap',
  'Printer', 'QrCode', 'Scan', 'Share', 'Star', 'Tag', 'Ticket', 'Play', 'Video',
  'ZoomIn', 'ZoomOut', 'Monitor', 'Building', 'Building2', 'ShoppingBag',
  'DollarSign', 'CreditCard', 'Wallet', 'Megaphone', 'Trophy', 'Target', 'Compass', 'Car',
  'GitBranch', 'FileEdit', 'FileSearch', 'Filter', 'SlidersHorizontal',
  'Clipboard', 'ClipboardCheck', 'ClipboardCopy', 'Table',
  'Maximize', 'Maximize2', 'Minimize',
  // 工作台扩展图标
  'Store', 'FilePlus', 'Hammer', 'ClipboardList', 'BarChart2',
  'FileCheck', 'ScrollText', 'Receipt',
  // 菜单图标扩展
  'Handshake', 'Activity', 'MessageSquare', 'MapPin',
  // 主题切换图标
  'Sun', 'Moon',
  // 水电煤仪表图标
  'Gauge', 'Banknote', 'CheckCheck',
  // 计划管理菜单图标
  'CalendarRange', 'ListChecks', 'Sliders',
  // 商户账号管理
  'UserCog',
  // 联营结算
  'Calculator',
  // 编码规则
  'Hash',
  // 计划详情操作
  'Send',
  // 商户旅程
  'Route',
  // 报表菜单图标
  'Square', 'Percent', 'ArrowUpRight', 'ArrowDownRight',
  // Phase 3-4 报表补充图标
  'CalendarDays', 'Undo2', 'ShieldCheck', 'DataAnalysis',
  'ShoppingCart',
  // 联营管理菜单图标
  'Users2',
  // 自营管理 & 其他页面补充图标
  'Archive', 'AtSign', 'Camera', 'CheckSquare', 'CircleSlash', 'CopyCheck',
  'CornerUpLeft', 'FileCode', 'FileImage', 'FileUp', 'Forward', 'GripVertical',
  'Hand', 'ImageOff', 'Link', 'Loader', 'Mail', 'Pause', 'Pencil', 'Rocket',
  'ThumbsDown', 'ThumbsUp', 'ToggleLeft', 'ToggleRight', 'UserPlus'
]

for (const iconName of commonLucideIcons) {
  if (LucideIcons[iconName]) {
    app.component(iconName, LucideIcons[iconName])
  }
}

// 全局属性
app.config.globalProperties.$ELEMENT = {
  size: 'default',
  zIndex: 3000
}

// 全局错误处理 - 防止未捕获的Vue错误导致页面白屏或刷新
app.config.errorHandler = (err, instance, info) => {
  console.error('[Vue Error Handler]', {
    error: err,
    message: err?.message,
    stack: err?.stack?.split('\n').slice(0, 5).join('\n'),
    component: instance?.$options?.name || instance?.$options?.__name || 'Unknown',
    info
  })
}

// 捕获Promise未处理的rejection
window.addEventListener('unhandledrejection', (event) => {
  console.error('[Unhandled Promise Rejection]', event.reason)
})

var pinia = createPinia()
app.use(pinia)
app.use(router)
app.use(i18n)
app.use(ElementPlus)

// 初始化设置（从 localStorage 恢复主题等偏好，必须在 pinia 注册后执行）
import { useSettingsStore } from '@/stores/settings'
useSettingsStore().init()
app.use(directives)

// 注册 VxeTable（高级表格组件）
setupVxeTable(app)

// 注册打印指令 v-print
app.directive('print', vPrint)

// 等待路由初始导航完成（动态路由加载）后再挂载，避免 "No match found" 警告
router.isReady().then(() => {
  app.mount('#app')
})
